/**
 * Apple App Store review account — narrow client gate for apple-review-auth Edge Function.
 * REVIEW-ONLY: remove after Apple approves the build.
 *
 * Client must NOT contain reviewer passwords or validate credentials locally.
 * Edge Function validates against APPLE_REVIEW_EMAIL / APPLE_REVIEW_PASSWORD secrets.
 */
export function isAppleReviewRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return (
    (path === '/auth' || path === '/auth/') &&
    new URLSearchParams(window.location.search).has('review')
  );
}

export function normalizeReviewerEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Route email/password sign-in through apple-review-auth only for the explicit
 * /auth?review route.
 *
 * Credentials are validated server-side only.
 */
export function shouldUseReviewerAuth(): boolean {
  return isAppleReviewRoute();
}

/** Email/password UI is available only on the explicit reviewer route. */
export function shouldShowEmailSignInOnAuthScreen(): boolean {
  return isAppleReviewRoute();
}

/** User-facing Apple sign-in failure copy (no secrets). */
export function getAppleSignInErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message === 'timeout') {
    return 'Sign in took too long. Please check your connection and try again.';
  }

  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
  const raw =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message: unknown }).message)
      : '';
  const lower = `${raw} ${code}`.toLowerCase();

  if (
    lower.includes('cancel') ||
    lower.includes('1001') ||
    lower.includes('user denied') ||
    lower.includes('authorization failed')
  ) {
    return 'Apple sign-in was cancelled. You can try again or sign in with email below.';
  }

  if (
    lower.includes('network') ||
    lower.includes('connect') ||
    lower.includes('internet') ||
    lower.includes('offline') ||
    lower.includes('1009') ||
    lower.includes('could not connect')
  ) {
    return 'Could not connect to Apple sign-in. Check your connection, then try again or use email sign-in below.';
  }

  return raw || 'Apple sign-in failed. Please try again or use email sign-in below.';
}
